import React from 'react'
import { Empty, Flex, List, Space } from 'antd'
import Avatar from '../../../../../../../../../components/common/avatar/Avatar'
import { getColorByIndex } from '../../../../../../../../../utils/colors.helper'
import Text from 'antd/es/typography/Text'
import { sqlToJsDate, sqlToTimeFromNow } from '../../../../../../../../../utils/date.helper'
import PatternIcon from '../../../../../../../../../components/common/PatternIcon'
import { ArrowRightOutlined } from '@ant-design/icons'
import {
    useFetchAnswerCategoryHistoryQuery,
    useFetchTextSubmissionDetailsQuery,
} from '../../../../../../../../../store/apis/submission.api'
import { useThemeColors } from '../../../../../../../../../hooks/useThemeColors'
import { isNumber } from 'lodash'
import {
    ActorTypes,
    getActorIcon,
    getActorName,
    NonHumanActors,
} from '../../../../../../../../../constants/actor-types'
import submissionSelectors from '../../../../../../../../../store/selectors/submission.selectors'
import { useSelector } from 'react-redux'
import { fetchTextSubmissionDetailsSelectors } from '../../../../../../../../../store/apis/utils/selectors/submission.api.selectors'
import { getConfidenceLevelNoun } from '../../../../../../../../../constants/confidence-levels'
import SubmissionApproveStatuses from '../../../../../../../../../constants/submission-approve-statuses'
import { highlightInlineDiff } from '../../../../../../../../../utils/text.helper'
import useRubricContext from '../../../../../../../../../hooks/useRubricContext'
import { useIsSenseUserOrImpersonated } from '../../../../../../../../../hooks/useIsSenseUserOrImpersonated'

const History = ({ answer, questionRubric }) => {
    const questionRubricId = questionRubric?.id || answer?.questionRubricId
    const { categories } = useRubricContext({ questionRubricId })
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const isSenseUser = useIsSenseUserOrImpersonated()

    const { data: reviews, isFetching } = useFetchAnswerCategoryHistoryQuery({ id: answer?.id, questionRubricId })

    const { data: latestReview } = useFetchTextSubmissionDetailsQuery(
        { submissionId: openedSubmissionId },
        {
            selectFromResult: result =>
                fetchTextSubmissionDetailsSelectors.answerByQuestionRubricId({ questionRubricId }, result),
        },
    )

    // Create a map of deleted category names from the history records
    const deletedCategoryNames = {}
    if (reviews) {
        reviews.forEach(review => {
            if (review.deleted && review.patternName && review.categoryId) {
                deletedCategoryNames[review.categoryId] = review.patternName
            }
        })
    }

    // Combine and process all reviews
    let allReviews = []
    if (latestReview && isNumber(latestReview.categoryId)) allReviews.push(latestReview)
    if (reviews) allReviews.push(...reviews)
    allReviews = groupByTrainingRunId(allReviews, isSenseUser)

    return (
        <List
            dataSource={allReviews}
            loading={{ spinning: isFetching, tip: 'Loading history...' }}
            locale={{ emptyText: <Empty description='The history is empty' /> }}
            renderItem={(review, index) => (
                <HistoryItem
                    review={review}
                    previousReview={allReviews[index + 1]}
                    categories={categories}
                    index={index}
                    deletedCategoryNames={deletedCategoryNames}
                    isSenseUser={isSenseUser}
                />
            )}
        />
    )
}

const HistoryItem = ({ review, previousReview, categories, deletedCategoryNames, isSenseUser }) => {
    const colors = useThemeColors()
    const isDeleted = review.deleted
    const newCategory = categories?.find(category => category.id === review.categoryId)
    const oldCategoryId = previousReview?.categoryId
    const oldCategory = categories?.find(category => category.id === oldCategoryId)
    const categoryChanged = oldCategoryId !== review.categoryId

    // Get the appropriate action description
    const userActionDescription = getUserActionDescription(review, categoryChanged, oldCategory, isDeleted)

    // Get actor details — Sense employees see the specific model type (ML/LLM), clients see generic name
    const actorName = isSenseUser ? getInternalActorName(review.actor) : getActorName(review.actor)
    const actorIcon = getActorIcon(review.actor)
    const actorColor = isNumber(review.actor?.id) ? getColorByIndex(review.actor?.id).value : colors.primary

    // Check if we can show diff (reasoning was updated and we have previous reasoning)
    const canShowDiff = review.reasoning != null && previousReview?.reasoning != null && review.reasoning !== previousReview.reasoning

    // Prepare the reasoning text to display - always show diff if available
    let reasoningContent = review.reasoning
    if (canShowDiff) {
        reasoningContent = highlightInlineDiff(previousReview.reasoning, review.reasoning)
    }

    return (
        <List.Item key={review.createdAt}>
            <Flex vertical gap={8}>
                {/* Actor and action information */}
                <div>
                    <Avatar icon={actorIcon} size='small' name={actorName} color={actorColor} className='mr-8' />
                    <Text strong>{actorName}</Text>
                    {' '}{userActionDescription}
                    <Text type='secondary' title={sqlToJsDate(review.createdAt)}>
                        {' '}
                        {sqlToTimeFromNow(review.createdAt)}
                    </Text>
                </div>

                {/* Category change information */}
                {(categoryChanged || isDeleted) && (
                    <div className='ml-4'>
                        <Space size={4}>
                            {renderCategoryChange(
                                isDeleted,
                                categoryChanged,
                                oldCategory,
                                oldCategoryId,
                                newCategory,
                                review,
                                deletedCategoryNames,
                            )}
                        </Space>
                    </div>
                )}

                {review.reasoning != null && (
                    <div className='ml-4'>
                        <Text italic type='secondary'>
                            {canShowDiff ? (
                                <span dangerouslySetInnerHTML={{ __html: reasoningContent }} />
                            ) : (
                                reasoningContent
                            )}
                        </Text>
                    </div>
                )}
            </Flex>
        </List.Item>
    )
}

// Returns a human-readable actor name for Sense employees, revealing the underlying model type
const getInternalActorName = actor => {
    if (actor === ActorTypes.ML_CREATED) return 'ML model'
    if (actor === ActorTypes.LLM) return 'LLM'
    return getActorName(actor)
}

// Helper function to determine the user action description
const getUserActionDescription = (review, categoryChanged, oldCategory, isDeleted) => {
    if (isDeleted) {
        return 'deleted the category'
    }

    if (categoryChanged && oldCategory) {
        return 'changed the category'
    }

    if (NonHumanActors.includes(review.actor)) {
        return isNumber(review.confidence)
            ? `classified with ${getConfidenceLevelNoun(review.confidence).toLowerCase()}`
            : 'classified'
    }

    if (review.reasoning != null) {
        return 'updated the personalized feedback'
    }

    if (review.approveStatus === SubmissionApproveStatuses.AUTO_APPROVED) {
        return 'auto-approved'
    }

    if (categoryChanged) {
        return isNumber(review.confidence)
            ? `set the category with ${getConfidenceLevelNoun(review.confidence).toLowerCase()}`
            : 'set the category'
    }

    return 'approved'
}

// Helper function to render the category change visualization
const renderCategoryChange = (
    isDeleted,
    categoryChanged,
    oldCategory,
    oldCategoryId,
    newCategory,
    review,
    deletedCategoryNames,
) => {
    // Show old category → new category for changes
    if (categoryChanged && !isDeleted && oldCategory) {
        return (
            <>
                <PatternIcon id={oldCategoryId} colorId={oldCategory.colorId} showId={false} />
                {oldCategory?.name || 'None'} <ArrowRightOutlined />
                {renderCategoryName(newCategory, review, deletedCategoryNames)}
            </>
        )
    }

    // Only show the category for deletions or simple settings
    return renderCategoryName(newCategory, review, deletedCategoryNames, isDeleted)
}

// Helper function to render a category name with appropriate styling
const renderCategoryName = (newCategory, review, deletedCategoryNames, isDeleted = false) => {
    if (isDeleted) {
        return (
            <>
                <PatternIcon id={review.categoryId} colorId={review.categoryId} showId={false} />
                <Text delete type='danger'>
                    {review.patternName || deletedCategoryNames[review.categoryId] || 'Unknown category'}
                </Text>
            </>
        )
    }

    if (newCategory) {
        return (
            <>
                <PatternIcon id={newCategory.id} colorId={newCategory.colorId} showId={false} />
                {newCategory.name}
            </>
        )
    }

    return (
        <>
            <PatternIcon id={review.categoryId} colorId={review.categoryId} showId={false} />
            {deletedCategoryNames[review.categoryId] || 'Unknown category'}
        </>
    )
}

// Group reviews by training run to consolidate AI assistant actions.
// Sense employees retain the original model actor (ML/LLM); clients see the generic "Sense Assistant".
const groupByTrainingRunId = (reviews, isSenseUser = false) => {
    const groupedReviews = []
    const seenTrainingRunIds = new Set()

    reviews.forEach(review => {
        if (isNumber(review.trainingRunId)) {
            if (!seenTrainingRunIds.has(review.trainingRunId)) {
                const groupedReview = {
                    ...review,
                    actor: isSenseUser ? review.actor : ActorTypes.SENSE_ASSISTANT,
                }
                groupedReviews.push(groupedReview)
                seenTrainingRunIds.add(review.trainingRunId)
            }
        } else {
            groupedReviews.push(review)
        }
    })

    return groupedReviews
}

export default History
